import { useCallback, useEffect, useMemo, useRef } from 'react'
import { DivIcon } from 'leaflet'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

interface UseRadarPointMapProps {
  points: any[]
  currentViewer: any
  isZoomOutMap: boolean
  handleMarkerClick: (point: any) => void
  povMarkerRef: React.RefObject<any>
}

const useRadarPointMap = ({
  points,
  currentViewer,
  isZoomOutMap,
  handleMarkerClick,
  povMarkerRef
}: UseRadarPointMapProps) => {
  const radarDragFlag = useRef({
    isDragging: false,
    rotate: 0,
    hgap: 0
  })

  const selectedPointDetail = useMemo(
    () => points.find((point) => point.id === currentViewer?.selectedPoint),
    [points, currentViewer?.selectedPoint]
  )

  // Get rotated marker icon
  const getRotatedMarkerIcon = useCallback(
    (rotationAngle: number, isZoomed: boolean) => {
      const iconSize = isZoomed ? 70 : 35
      const iconHeight = isZoomed ? 50 : 25
      const iconAnchorX = iconSize / 2
      const iconAnchorY = iconHeight

      const icon = new DivIcon({
        iconUrl: '/assets/radar-icon.png',
        iconAnchor: [iconAnchorX, iconAnchorY],
        className: 'rotating-marker',
        html: `<img
          src="/assets/radar-icon.png"
          draggable="false"
          style="clip-path: polygon(0 0, 50% 100%, 100% 0);
          height:${iconHeight}px !important;
          width:${iconSize}px !important;
          transform: rotate(${rotationAngle}deg);
          transform-origin: 50% 100%;
          opacity: 0.35;"
        />`
      })
      return icon
    },
    []
  )

  const lastPointIdRef = useRef<string | null>(null)

  // Reinitialize radar rotation whenever the selected point or active image changes
  useEffect(() => {
    const currentPointId = selectedPointDetail?.id
    const hasPointChanged = lastPointIdRef.current !== currentPointId

    const pointObj =
      (currentViewer?.pointDetail?.id === selectedPointDetail?.id
        ? currentViewer.pointDetail
        : selectedPointDetail) || selectedPointDetail
    const activeImageId = currentViewer?.selectedImage || pointObj?.image360?.id
    const effectiveHGap = Krpano.GetEffectiveHGap(pointObj, activeImageId)

    radarDragFlag.current.hgap = effectiveHGap

    if (hasPointChanged) {
      radarDragFlag.current.isDragging = false
      lastPointIdRef.current = currentPointId

      if (!selectedPointDetail || !selectedPointDetail.image360) {
        radarDragFlag.current.rotate = 0
      } else {
        const baseRotate = Krpano.GetFrontYaw(pointObj, activeImageId)
        radarDragFlag.current.rotate = Krpano.NormalizeHGap(
          baseRotate + effectiveHGap
        )
      }
    } else if (povMarkerRef?.current) {
      // When sub-image switches within same point, keep the current world radar angle
      if (
        radarDragFlag.current.rotate !== undefined &&
        radarDragFlag.current.rotate !== null
      ) {
        povMarkerRef.current.setIcon(
          getRotatedMarkerIcon(radarDragFlag.current.rotate, isZoomOutMap)
        )
      }
    }
  }, [
    selectedPointDetail,
    currentViewer?.pointDetail,
    currentViewer?.selectedImage,
    getRotatedMarkerIcon,
    isZoomOutMap,
    povMarkerRef
  ])

  // Check if point is in triangle
  const isPointInTriangle = (
    px: number,
    py: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
    cx: number,
    cy: number
  ) => {
    const v0x = cx - ax
    const v0y = cy - ay
    const v1x = bx - ax
    const v1y = by - ay
    const v2x = px - ax
    const v2y = py - ay

    const dot00 = v0x * v0x + v0y * v0y
    const dot01 = v0x * v1x + v0y * v1y
    const dot02 = v0x * v2x + v0y * v2y
    const dot11 = v1x * v1x + v1y * v1y
    const dot12 = v1x * v2x + v1y * v2y

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01)
    const u = (dot11 * dot02 - dot01 * dot12) * invDenom
    const v = (dot00 * dot12 - dot01 * dot02) * invDenom

    return u >= 0 && v >= 0 && u + v <= 1
  }

  // Get point area based on zoom state
  const getPointArea = (isZoomed: boolean) => {
    const pointSize = isZoomed ? 35 : 20
    const pointRadius = pointSize / 2
    return {
      width: pointSize,
      height: pointSize,
      radius: pointRadius
    }
  }

  // Get radar area based on zoom state
  const getRadarArea = (isZoomed: boolean) => {
    const iconWidth = isZoomed ? 70 : 35
    const iconHeight = isZoomed ? 50 : 25
    return {
      width: iconWidth,
      height: iconHeight,
      halfWidth: iconWidth / 2
    }
  }

  // Check if click is in point area
  const isClickInPointArea = (
    clickLat: number,
    clickLng: number,
    pointLat: number,
    pointLng: number,
    isZoomed: boolean
  ): boolean => {
    const pointArea = getPointArea(isZoomed)
    const latDiff = Math.abs(clickLat - pointLat)
    const lngDiff = Math.abs(clickLng - pointLng)
    return Math.sqrt(latDiff * latDiff + lngDiff * lngDiff) <= pointArea.radius
  }

  // Check if click is in radar area
  const isClickInRadarArea = (
    clickLat: number,
    clickLng: number,
    radarLat: number,
    radarLng: number,
    isZoomed: boolean
  ): boolean => {
    const radarArea = getRadarArea(isZoomed)
    const topX = radarLng
    const topY = radarLat - radarArea.height
    const leftX = radarLng - radarArea.halfWidth
    const leftY = radarLat
    const rightX = radarLng + radarArea.halfWidth
    const rightY = radarLat

    return isPointInTriangle(
      clickLng,
      clickLat,
      topX,
      topY,
      leftX,
      leftY,
      rightX,
      rightY
    )
  }

  // Map event handlers
  const mapEventHandler = useCallback(
    () => ({
      mousemove(e: any) {
        if (radarDragFlag.current.isDragging && currentViewer.pointDetail) {
          const newHlookat = Krpano.GetAngle(
            e.latlng.lat - currentViewer.pointDetail.y,
            e.latlng.lng - currentViewer.pointDetail.x
          )
          radarDragFlag.current.rotate = newHlookat

          if (povMarkerRef.current) {
            povMarkerRef.current.setIcon(
              getRotatedMarkerIcon(newHlookat, isZoomOutMap)
            )
          }

          const krpano = new KrpanoEngine('viewer').instance
          krpano?.set('view.hlookat', newHlookat)
        }
      },
      mouseup() {
        if (radarDragFlag.current.isDragging && currentViewer.pointDetail) {
          radarDragFlag.current.isDragging = false
          const hlookat = Krpano.NormalizeHGap(
            new KrpanoEngine('viewer').instance?.get('view.hlookat')
          )
          radarDragFlag.current.hgap = Krpano.NormalizeHGap(
            radarDragFlag.current.rotate - hlookat
          )
        }
      },
      click: (e: any) => {
        const clickLat = e.latlng.lat
        const clickLng = e.latlng.lng

        const pointsUnderClick = points.filter((point) => {
          return isClickInPointArea(
            clickLat,
            clickLng,
            point.y,
            point.x,
            isZoomOutMap
          )
        })

        if (pointsUnderClick.length > 0) {
          let closestPoint = pointsUnderClick[0]
          let minDistance = Infinity

          pointsUnderClick.forEach((point) => {
            const latDiff = Math.abs(clickLat - point.y)
            const lngDiff = Math.abs(clickLng - point.x)
            const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff)

            if (distance < minDistance) {
              minDistance = distance
              closestPoint = point
            }
          })

          handleMarkerClick(closestPoint)
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentViewer.pointDetail, points, isZoomOutMap, handleMarkerClick]
  )

  // Radar event handlers
  const radarEventHandler = useCallback(
    () => ({
      mouseover: (e: any) => {
        const radar = currentViewer.pointDetail
        if (!radar) return

        const mouseLat = e.latlng.lat
        const mouseLng = e.latlng.lng

        const overlappingPoint = points.find((point) => {
          return (
            point.id !== radar.id &&
            isClickInRadarArea(
              mouseLat,
              mouseLng,
              radar.y,
              radar.x,
              isZoomOutMap
            )
          )
        })

        if (e.originalEvent?.target) {
          e.originalEvent.target.style.cursor = overlappingPoint
            ? 'pointer'
            : 'grab'
        }
      },
      mousedown: (e: any) => {
        if (e.originalEvent) {
          e.originalEvent.stopPropagation()
        }

        const radar = currentViewer.pointDetail
        if (!radar) return

        const clickLat = e.latlng.lat
        const clickLng = e.latlng.lng

        const isInRadarArea = isClickInRadarArea(
          clickLat,
          clickLng,
          radar.y,
          radar.x,
          isZoomOutMap
        )

        if (isInRadarArea) {
          const pointUnderMousedown = points.find((point) => {
            return (
              point.id !== radar.id &&
              isClickInPointArea(
                clickLat,
                clickLng,
                point.y,
                point.x,
                isZoomOutMap
              )
            )
          })

          if (!pointUnderMousedown) {
            radarDragFlag.current.isDragging = true
            if (e.originalEvent?.target) {
              e.originalEvent.target.style.cursor = 'grabbing'
            }
          }
        }
      },
      mouseup: (e: any) => {
        if (radarDragFlag.current.isDragging) {
          radarDragFlag.current.isDragging = false
          if (e.originalEvent?.target) {
            const radar = currentViewer.pointDetail
            if (radar) {
              const overlappingPoint = points.find(
                (point) =>
                  point.id !== radar.id &&
                  isClickInRadarArea(
                    e.latlng.lat,
                    e.latlng.lng,
                    radar.y,
                    radar.x,
                    isZoomOutMap
                  )
              )
              e.originalEvent.target.style.cursor = overlappingPoint
                ? 'pointer'
                : 'grab'
            }
          }
        }
      },
      dblclick: (e: any) => {
        if (e.originalEvent) {
          e.originalEvent.stopPropagation()
          e.originalEvent.preventDefault()
        }
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentViewer.pointDetail, points, isZoomOutMap]
  )

  // Generate radar marker
  const radarMaker = useMemo(() => {
    const pointDetail = selectedPointDetail

    if (pointDetail && pointDetail.image360) {
      const krpanoEl = new KrpanoEngine('viewer').instance
      let rotate = 0

      const isExplicitSelection =
        currentViewer?.isSelectPointInLeftMenu ||
        currentViewer?.isClickFromTourCarousel ||
        currentViewer?.isSelectPoint

      const pointObj =
        (currentViewer?.pointDetail?.id === pointDetail.id
          ? currentViewer.pointDetail
          : pointDetail) || pointDetail
      const activeImageId =
        currentViewer?.selectedImage || pointObj.image360?.id

      const isSamePoint =
        lastPointIdRef.current === pointDetail.id &&
        currentViewer?.pointDetail?.id === pointDetail.id

      const isSettled = !currentViewer?.imageIsLoading && krpanoEl
      if (isSettled && !isExplicitSelection) {
        const hlookat = krpanoEl.get('view.hlookat')
        if (radarDragFlag.current.isDragging && radarDragFlag.current.rotate) {
          rotate = radarDragFlag.current.rotate
        } else {
          rotate = Krpano.GetRadarAngle(hlookat, pointObj, activeImageId)
          radarDragFlag.current.rotate = rotate
        }
      } else if (
        isSamePoint &&
        radarDragFlag.current.rotate !== undefined &&
        radarDragFlag.current.rotate !== null
      ) {
        // Within same point, preserve current radar angle during sub-image transitions
        rotate = radarDragFlag.current.rotate
      } else {
        // When transitioning to a new point or loading, strictly use active image default view until settled
        const baseRotate = Krpano.GetFrontYaw(pointObj, activeImageId)
        rotate = Krpano.GetRadarAngle(baseRotate, pointObj, activeImageId)
        radarDragFlag.current.rotate = rotate
      }

      return {
        position: [pointDetail.y, pointDetail.x],
        rotate,
        zIndexOffset: 999
      }
    }
    return null
  }, [
    selectedPointDetail,
    currentViewer?.pointDetail,
    currentViewer?.selectedImage,
    currentViewer?.imageIsLoading,
    currentViewer?.isSelectPoint,
    currentViewer?.isSelectPointInLeftMenu,
    currentViewer?.isClickFromTourCarousel
  ])

  // Sync 360 view to map radar
  const sync360ViewToArc = useCallback(
    (icon: (angle: number) => DivIcon) => {
      ;(
        window as unknown as { sync_360viewToArc: (hlookat: number) => void }
      ).sync_360viewToArc = (hlookat: number) => {
        const krpano = new KrpanoEngine('viewer')
        if (krpano.instance) {
          if (!krpano.isLoadComplete) {
            return
          }

          const activePoint = krpano.getActivePoint(points)
          const activeImageId = krpano.renderedImageId
          if (activePoint && activeImageId) {
            radarDragFlag.current.hgap = Krpano.GetEffectiveHGap(
              activePoint,
              activeImageId
            )
          }
        }

        const h = Krpano.NormalizeHGap(hlookat)
        radarDragFlag.current.rotate = Krpano.NormalizeHGap(
          h + radarDragFlag.current.hgap
        )
        povMarkerRef?.current?.setIcon(icon(radarDragFlag.current.rotate))
      }
    },
    [points, povMarkerRef]
  )

  return {
    getRotatedMarkerIcon,
    mapEventHandler,
    radarEventHandler,
    radarMaker,
    sync360ViewToArc
  }
}

export default useRadarPointMap
